import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/70 via-teal-800/60 to-teal-700/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            Real advice to help you make better choices about drinking
          </h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            Cheers is a social change initiative to create a healthier, safer and more responsible drinking culture in New Zealand.
          </p>
          <Link to="/tips">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl">
              Explore Our Tips
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our mission is to create a healthier, safer and more responsible drinking culture
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We provide evidence-based information and practical tools to help New Zealanders make informed decisions about alcohol consumption.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 - Parents and Teens */}
            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1520857014576-2c4f4c972b57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85"
                  alt="Parents and teens"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6 bg-teal-600 text-white">
                <h3 className="text-2xl font-bold mb-3">Have the talk</h3>
                <p className="mb-4">Step up and talk to your teen about drinking. Get practical tips and conversation starters.</p>
                <Link to="/parents-teens">
                  <Button variant="secondary" className="w-full group-hover:bg-white group-hover:text-teal-600 transition-colors">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Card 2 - Standard Drinks */}
            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1707941391936-bbbfa2b15758?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Standard drinks"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6 bg-cyan-600 text-white">
                <h3 className="text-2xl font-bold mb-3">Know your standards</h3>
                <p className="mb-4">A standard drink comes in different shapes and sizes. Do you know how much you're drinking?</p>
                <Link to="/standard-drinks">
                  <Button variant="secondary" className="w-full group-hover:bg-white group-hover:text-cyan-600 transition-colors">
                    Find Out <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Card 3 - Balanced Lifestyle */}
            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1477332552946-cfb384aeaf1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxoZWFsdGglMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NzA3NjE1NjZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Balanced lifestyle"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6 bg-teal-700 text-white">
                <h3 className="text-2xl font-bold mb-3">Keep it balanced</h3>
                <p className="mb-4">Learn how moderate drinking can fit into a healthy, balanced lifestyle.</p>
                <Link to="/balanced-lifestyle">
                  <Button variant="secondary" className="w-full group-hover:bg-white group-hover:text-teal-700 transition-colors">
                    Read More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SMASHED Project */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-10 bg-gradient-to-br from-teal-100 to-cyan-100 flex flex-col justify-center">
                <BookOpen className="h-12 w-12 text-teal-600 mb-4" />
                <h3 className="text-3xl font-bold text-gray-900 mb-4">SMASHED Project</h3>
                <p className="text-lg text-gray-700 mb-6">
                  An internationally-acclaimed theatre-in-education programme that addresses the social dangers of teenage drinking and seeks to reduce alcohol-related harm in young people.
                </p>
                <Link to="/smashed">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                    Discover More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="h-80 md:h-auto">
                <img 
                  src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85"
                  alt="SMASHED Project"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Pregnancy Warning */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="h-80 md:h-auto">
                <img 
                  src="https://images.unsplash.com/photo-1716810406166-2eee03e0bbc3?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Pregnancy and alcohol"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-10 bg-gradient-to-br from-rose-100 to-pink-100 flex flex-col justify-center">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Alcohol and pregnancy don't mix</h3>
                <p className="text-lg text-gray-700 mb-6">
                  There is no known safe level of alcohol consumption during pregnancy. Protecting your baby starts with informed choices.
                </p>
                <Link to="/pregnancy">
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white">
                    Learn Why <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

    </div>
  );
};

export default Home;